<script lang="ts">
import { Cookies } from '@/shared/helpers/Cookies'
import { ContactInfo } from '@/shared/interfaces/ContactInfo'
import {Message, MessageGroup} from '@/shared/interfaces/Message'
import { User } from '@/shared/interfaces/User'
import { ChatsStore } from '@/store/modules/Chats/ChatsStore'
import { chatsAddMessageToChat } from '@/store/modules/Chats/Methods/chatsAddMessageToChat'
import { chatsGetAll } from '@/store/modules/Chats/Methods/chatsGetAll'
// import { chatsMarkMessagesAsRead } from "@/store/modules/Chats/Methods/chatsMarkMessagesAsRead";
import { chatsRefresh } from '@/store/modules/Chats/Methods/chatsRefresh'
import { chatsSetUserOnline } from '@/store/modules/Chats/Methods/chatsSetUserOnline'
import { chatsUpdateMessageStatus } from '@/store/modules/Chats/Methods/chatsUpdateMessageStatus'
import { chatsUpdateUserProfile } from '@/store/modules/Chats/Methods/chatsUpdateUserProfile'
import { ContactsStore } from '@/store/modules/Contacts/ContactsStore'
import { contactsSetUserOnline } from '@/store/modules/Contacts/Methods/contactsSetUserOnline'
import { contactsUpdateProfile } from '@/store/modules/Contacts/Methods/contactsUpdateProfile'
import { FoldersStore } from '@/store/modules/Folders/FoldersStore'
import { PlatformStore } from '@/store/modules/Platform/PlatformStore'
import { principalRefresh } from '@/store/modules/Principal/Methods/principalRefresh'
import { PrincipalStore } from '@/store/modules/Principal/PrincipalStore'
import { defineComponent } from 'vue-demi'
import { Api } from '../api/Api'
import { Folder } from '../interfaces/Folder'
import {CallInfo, CallsStore} from '@/store/modules/Calls/CallsStore'
import {chatsGetById} from '@/store/modules/Chats/Methods/chatsGetById'
import router from '@/router'
import { focusExistingWindow } from '@/shared/helpers/focusExistingWindow'

function getLastMessage(groups: any): Message | null {
  const allMessages = groups.flatMap((group: MessageGroup) => group.messages)
  if (allMessages.length === 0) return null

  return allMessages.reduce((latest: Message, current: Message) => {
    const currentTime = parseDateTime(current.dt_departure)
    const latestTime = parseDateTime(latest.dt_departure)
    return currentTime > latestTime ? current : latest
  })
}

function parseDateTime(dateTimeStr: string): number {
  const [time, date] = dateTimeStr.split(' ')
  const [day, month, year] = date.split('.')
  const [hours, minutes, seconds] = time.split(':')
  return new Date(
      parseInt(year),
      parseInt(month) - 1,
      parseInt(day),
      parseInt(hours),
      parseInt(minutes),
      parseInt(seconds),
  ).getTime()
}

export interface WebSocketMessage {
  status: number;
  message: {
    answer_to: Message;
    type: 'update' | string;
    topic:
      | 'user_online'
      | 'receive_messages'
      | 'contact_notification'
      | 'chat_notification'
      | 'profile_update_notification'
      | 'messages_status_update'
      | 'mark_messages_as_read'
      | 'change_auth_user_id'
      // | "groups_notification"
      | 'message_deleted'
      | 'message_updated'
      | 'call_notification'
      | 'folders_notification';
    action_type: string;
    chat_id: number;
    chat: any;
    group_id: number;
    dt_last_online: string;
    contact: ContactInfo;
    user_id: number;
    online: number;
    user: User;
    message_id: number;
    status: number;
    folders: Folder[];
    message: Message;
    chat_name: string | null
  };
}

export default defineComponent({
  components: {},
  setup() {
    const { contactsStore } = ContactsStore.get()
    const { principalState } = PrincipalStore.get()
    const { platformState } = PlatformStore.get()
    const { chatsState, chatsStore } = ChatsStore.get()

    return {
      contactsStore,
      platformState,
      principalState,

      chatsStore,
      chatsState,
    }
  },
  mounted() {
    this.connectNotifications()

    this.connectToWebSocket()
  },
  methods: {
    connectNotifications() {
      // Уведомления не поддерживаются, расходимся.
      if (!('Notification' in window)) {
        return
      }

      // Уведомления уже подключены, ничего делать не нужно.
      if (Notification.permission === 'granted') {
        return
      }

      // Уведомления еще не разрешались, спрашиваем о них.
      if (Notification.permission !== 'denied') {
        Notification.requestPermission()
      }
    },
    handleCallNotification(message: any) {
      const {callState, callStore} = CallsStore.get()
      const cookieOptions = {
        domain: window.location.hostname,
        path: '/',
      }
      const currentUserId = this.principalState.user.id
      const { action, call_id, caller, chat_id, call_type } = message

      switch (action) {
        case 'incoming_call':
          callStore.dispatch(async (state) => {
            state.incomingCall = {
              userId: +currentUserId,
              callId: call_id,
              chatId: chat_id,
              callerId: caller.id,
              type: 'one-to-one',
              call_type: call_type,
              status: 'incoming',
              liveKitRoomName: '',
              liveKitToken: '',
              liveKitUrl: '',
            }
          })

          window.dispatchEvent(new CustomEvent('show-incoming-call', { detail: callState.incomingCall }))
          if ('Notification' in window && this.principalState.user.is_notifications_active === 1) {
            if (Notification?.permission !== 'granted') {
              this.connectNotifications()
            }
            const notification = new Notification('Входящий звонок', {
              body: 'Нажмите, чтобы ответить',
              silent: false,
              tag: 'incoming-call',
            })

            notification.onclick = () => {
              focusExistingWindow()
              notification.close()
            }
          }
          break
        case 'call_accepted':
          if (callState.outgoingCall?.callId === call_id) {
            const activeCall = { ...callState.outgoingCall, status: 'active' as const, date: new Date() }
            callStore.dispatch(async (state) => {
              state.activeCall = activeCall as CallInfo
              state.outgoingCall = null
              state.incomingCall = null
            })
            Cookies.set('call', JSON.stringify(callState.activeCall), cookieOptions)
          } else {
            callStore.dispatch(async (state) => {
              state.incomingCall = null
            })
            window.dispatchEvent(new CustomEvent('show-incoming-call', { detail: null }))
          }
          break
        case 'call_rejected':
          if (callState.incomingCall?.callId === call_id || callState.outgoingCall?.callId === call_id || callState.activeCall?.callId === call_id) {
            callStore.dispatch(async (state) => {
              state.outgoingCall = null
              state.activeCall = null
              state.incomingCall = null
            })
            window.dispatchEvent(new CustomEvent('show-incoming-call', { detail: null }))
          }
          Cookies.remove('call', cookieOptions)
          break
        case 'incoming_call_expired':
        case 'missed_call':
        case 'call_missed':
        case 'call_cancelled':
          if (callState.incomingCall?.callId === call_id || callState.outgoingCall?.callId === call_id || callState.activeCall?.callId === call_id) {
            callStore.dispatch(async (state) => {
              state.outgoingCall = null
              state.activeCall = null
              state.incomingCall = null
            })
            window.dispatchEvent(new CustomEvent('show-incoming-call', { detail: null }))
            // addStatusCallMessage(chat_id, currentUserId, chatsState.chats.find((chat) => chat.id === chat_id).user);
          }
          Cookies.remove('call', cookieOptions)
          break
        case 'call_ended':
          if (callState.activeCall?.callId === call_id || callState.outgoingCall?.callId === call_id || callState.incomingCall?.callId === call_id) {
            callStore.dispatch(async (state) => {
              state.outgoingCall = null
              state.activeCall = null
              state.incomingCall = null
            })
            window.dispatchEvent(new CustomEvent('show-incoming-call', { detail: null }))

            // window.dispatchEvent(new CustomEvent('call-ended'));
          }

          Cookies.remove('call', cookieOptions)
          break
      }
    },
    async onContactNotification(contact: ContactInfo) {
      await this.chatsStore.dispatch(async (state) => {
        const chat = state.chats.filter(
          (s) => s.contact && s.contact.id === contact.id,
        )[0]

        if (chat) {
          chat.contact = contact
        }
      })

      this.contactsStore.dispatch(async (state) => {
        const con = state.contacts.filter(
          (s) => s.contact && s.contact.id === contact.id,
        )[0]

        if (con) {
          con.contact = contact
        }
      })
    },
    async onSocketMessage(event: any) {
      const eventData: WebSocketMessage = JSON.parse(event.data)
      console.log(eventData)
      // let goToContact = 0
      if (
        !eventData ||
        !eventData.message ||
        eventData.message.type !== 'update'
      ) {
        return
      }

      if (eventData.message.action_type === 'notifications_toggled') {
        await this.chatsStore.dispatch(async (state) => {
          const chat = state.chats.find(
              (s) =>
                  s.id === eventData.message?.chat?.id ||
                  s.id === eventData.message?.group_id,
          )
          if (chat) {
            chat.notifications_enabled = eventData.message.chat.notifications_enabled
            if (chat.group_info) {
              chat.group_info.notifications_enabled = eventData.message.chat.notifications_enabled
            }
          }
        })
        return
      }

      if (
        // eventData.message.topic === "chat_notification" ||
        eventData.message.action_type === 'update'
      ) {
        if (!eventData.message.chat.is_participant) {
          return
        }
        await this.chatsStore.dispatch(async (state) => {
          const chat = state.chats.find(
            (s) =>
              s.id === eventData.message?.chat?.id ||
              s.id === eventData.message?.group_id,
          )
          if (chat && eventData.message?.chat?.group_info) {
            chat.group_info = eventData.message.chat.group_info
          }
        })
      }
      if (
          eventData.message.topic === 'contact_notification' &&
          eventData.message.action_type === 'create'
      ) {
        await this.chatsStore.dispatch(async (state) => {
          const chat = state.chats.find(
              (s) =>
                  s.id === eventData.message?.chat?.id ||
                  s.id === eventData.message?.group_id,
          )

          if (!chat && eventData.message.chat?.id) {
            state.chats = [eventData.message.chat, ...state.chats]
          }
          //сделать один чат подгрузку и добавить его
          await chatsRefresh()
          const findChat = this.chatsState.chats.find(item => item.contact?.id === eventData.message.contact.id && item.chat_type === 'private')
          if (findChat) {
            await router.push(`/chats/${findChat.id}`)
          }
        })
      } else if (
        // eventData.message.topic === "chat_notification" ||
        eventData.message.action_type === 'create'
      ) {
        await this.chatsStore.dispatch(async (state) => {
          const chat = state.chats.find(
            (s) =>
              s.id === eventData.message?.chat?.id ||
              s.id === eventData.message?.group_id,
          )

          if (!chat && eventData.message.chat?.id) {
            state.chats = [eventData.message.chat, ...state.chats]
          }
        })
      }

      if (eventData.message.action_type === 'update_groups_online') {
        const groupId: number = (eventData.message as any).group_id
        const usersCount: number = (eventData.message as any).users_count
        const usersOnline: number = (eventData.message as any).users_online

        await this.chatsStore.dispatch(async (state) => {
          const chat = state.chats.filter(
            (s) => s.group_info && s.id === groupId,
          )[0]

          if (chat && chat.group_info) {
            chat.group_info.users_count = usersCount
            chat.group_info.users_online = usersOnline
          }
        })
      }

      if (eventData.message.topic === 'folders_notification') {
        const foldersStore = FoldersStore.get().foldersStore

        await foldersStore.dispatch(async (state) => {
          state.folders = eventData.message.folders.sort(
            (a, b) => a.sorting_number - b.sorting_number,
          )

          if (state.selectedFolder) {
            state.selectedFolder = state.folders.find(
              (s) => s.id === state.selectedFolder?.id,
            )
          }
        })
      }
      if (eventData.message.topic === 'message_updated') {
        if (this.chatsState.selectedChat?.id === eventData.message.chat_id) {
          this.chatsState.messageGroups.forEach(item => {
            const message = item.messages.findIndex(item => item.id === eventData.message.message?.id)
            if (message !== -1) {
              item.messages[message] = {...eventData.message.message}
            }
          })
        } else {
          await chatsRefresh()
        }
      }
      else if (eventData.message.topic === 'call_notification') {
        this.handleCallNotification(eventData.message)
      }
      if (eventData.message.topic === 'message_deleted') {
        if (this.chatsState.selectedChat?.id === eventData.message.chat_id) {
          const findMessageGroups = this.chatsState.messageGroups.findIndex(item =>
            item.messages.find(item => item.id === eventData.message.message_id),
          )
          if (findMessageGroups !== -1) {
            this.chatsState.messageGroups[findMessageGroups].messages = this.chatsState.messageGroups[findMessageGroups].messages.filter(item => item.id !== eventData.message.message_id )
            if (!this.chatsState.messageGroups[findMessageGroups].messages.length) {
              this.chatsState.messageGroups.splice(findMessageGroups, 1)
            }
          }
          const chat = this.chatsState.chats.find(item => item.id === eventData.message.chat_id)
          const lastMessage = getLastMessage(this.chatsState.messageGroups)
          if (lastMessage && chat) {
            chat.last_message = lastMessage
          } else if (chat) {
            chat.last_message = null
          }
        } else {
          await chatsRefresh()
        }
      }

      if (eventData.message.topic === 'receive_messages') {
        const {callState, callStore} = CallsStore.get()
        if (eventData.message.message.text === 'Звонок завершён' && eventData.message.user.name === 'Система' && (callState.activeCall || callState.incomingCall)) {
          await callStore.dispatch(async (state) => {
            state.outgoingCall = null
            state.activeCall = null
            state.incomingCall = null
          })
          window.dispatchEvent(new CustomEvent('show-incoming-call', { detail: null }))
          // window.dispatchEvent(new CustomEvent('call-ended'));
        }
        if (eventData.message.user_id === this.chatsState.userId) {
          return
        }

        const chatId = eventData.message.chat_id

        let chat = this.chatsState.chats.filter((s) => s?.id === chatId)[0]

        if (!chat) {
          const newChats = await chatsGetAll()

          chat = newChats.filter((s) => s.id === chatId)[0]

          if (!chat) {
            return
          }
          this.chatsState.chats = [chat, ...this.chatsState.chats]
        }

        if (this.chatsState.selectedChat?.id === chatId) {
          const loadMessages = chat.group_info
              ? Api.messages.getFromGroupChat
              : Api.messages.get

          const response = await loadMessages(
              chatId,
              0,
              1,
              eventData.message.chat?.chat_type,
          )
          const message = response.messages.filter(
              (s) => s.id === eventData.message.message.id,
          )[0]

          if (!message) {
            return
          }

          await chatsAddMessageToChat(
              eventData.message.chat_id,
              message,
              eventData.message.user,
          )

          // const minChatScrollPosition = -700
          // // let chatScrollPosition = minChatScrollPosition - 1
          // //
          // // try {
          // //   chatScrollPosition =
          // //       document.querySelector('.cMessageGroups')?.scrollTop || minChatScrollPosition - 1
          // // } catch (e) {
          // //   console.error(e)
          // // }
        } else {
          const updatedChat = await chatsGetById(chatId)
          const findIndex = this.chatsState.chats.findIndex(item => item.id === chatId)
          if (findIndex !== -1) {
            this.chatsState.chats[findIndex] = {...updatedChat}
          }
        }

        // if (
        //   this.platformState.isTabActive &&
        //   this.chatsState.selectedChat &&
        //   this.chatsState.selectedChat.id === eventData.message.chat_id &&
        //   chatScrollPosition > minChatScrollPosition
        // ) {
        //   await chatsMarkMessagesAsRead([eventData.message.chat_id]);
        // }

        if (
          'Notification' in window &&
          Notification.permission === 'granted' &&
          this.principalState.user.is_notifications_active === 1 &&
          this.chatsState.chats.filter(
            (s) => s?.user && s.user.id === eventData.message.user.id,
          ).length &&
          eventData.message.user.id !== this.principalState.user.id &&
            eventData.message.chat.notifications_enabled
        ) {
          let username =
            eventData.message.user.name + ' ' + eventData.message.user.surname

          if (eventData.message.contact && eventData.message.contact.name) {
            username =
              eventData.message.contact.name +
              ' ' +
              eventData.message.contact.surname
          }
          if (eventData.message.chat.chat_type !== 'private') {
            username = eventData.message.chat_name || ''
          }
          const notification = new Notification(username, {
            body: eventData.message.message.text ? eventData.message.message.text : 'Вам отправлено новое сообщение' ,
            tag: `chat-${eventData.message.chat_id}`,
          })
          notification.onclick = () => {
            focusExistingWindow()
            notification.close()
          }
        }
      } else if (eventData.message.topic === 'user_online') {
        await chatsSetUserOnline(
          eventData.message.user_id,
          eventData.message.online,
          eventData.message.dt_last_online,
        )
        await contactsSetUserOnline(
          eventData.message.user_id,
          eventData.message.online,
          eventData.message.dt_last_online,
        )
      } else if (eventData.message.topic === 'messages_status_update') {
        if (eventData.message.chat_id === this.chatsState.selectedChat?.id) {
          await chatsUpdateMessageStatus(
              eventData.message.chat_id,
              eventData.message.message_id,
              eventData.message.status,
          )
        }
      } else if (eventData.message.topic === 'profile_update_notification') {
        await chatsUpdateUserProfile(eventData.message.user)
        await contactsUpdateProfile(
          eventData.message.user.id,
          eventData.message.user,
        )
        await principalRefresh()
      } else if (eventData.message.topic === 'contact_notification') {
        await this.onContactNotification(eventData.message.contact)
      } else if (eventData.message.topic === 'chat_notification') {
        await chatsRefresh()
      }
    },
    connectToWebSocket() {
      const userId = +Cookies.get('userId')

      if (!userId) {
        return
      }

      const socket = new WebSocket(`wss://dt.softwarecenter.ru/ws/${userId}/`)

      socket.onopen = () => {
        if (Cookies.get('authToken')) {
          socket.send(
            JSON.stringify({
              type: 'action',
              topic: 'set_auth_token',
              auth_token: Cookies.get('authToken'),
            }),
          )
        }

        socket.send(
          JSON.stringify({
            type: 'action',
            topic: 'set_client_platform',
            client_platform: 'web',
          }),
        )

        socket.send(
          JSON.stringify({
            type: 'subscribe',
            topic:
              'user_online,receive_messages,contact_notification,chat_notification,profile_update_notification,messages_status_update,mark_messages_as_read,change_auth_user_id,folders_notification,call_notification',
          }),
        )
      }

      socket.onmessage = this.onSocketMessage

      socket.onclose = (e: any) => {
        console.log(
          'Socket is closed. Reconnect will be attempted in 1 second.',
          e.reason,
        )
        setTimeout(() => {
          this.connectToWebSocket()
        }, 1000)
      }

      socket.onerror = (err: any) => {
        console.error(
          'Socket encountered error: ',
          err.message,
          'Closing socket',
        )
        socket.close()
      }
    },
  },
})
</script>

<template>
  <div class="web-socket-manager" />
</template>
